import _ from 'lodash'
import { createSelector } from 'reselect'

import DEFAULTS from 'core/data-model/constants/defaults'
import CONSTANTS from 'core/data-model/constants/values'
import { isValidAge } from 'core/service/insured/birthdate'
import { roundUp } from 'core/service/lib/number-format'
import { isNil, isNotNil } from 'core/service/lib/type-check'
import { getAge } from 'quick-quote/insured-information/selectors'
import {
  getSumAssured$,
  getSelectedRiders,
  getBasicPremium$,
  getTotalPremium$,
  getBasicPlanErrorMessage,
} from 'quick-quote/product-common/coverage-plan/selectors'
import { getBasicPlanCodeForMRTA } from 'quick-quote/product-mrta/coverage-plan/selectors.utils'
import { getSelectedDisplayProduct, getDisplayProducts } from 'quick-quote/product-selection/selectors'
import { canProceedToCoveragePlan } from 'quick-quote/selectors/next-tab-proceeding'

// import { getLoanAmount, getLoanTerm, getCoverageTerm } from 'quick-quote/product-loan/coverage-plan/redux'

export const getLoanAmount = (state) => state.loan.coveragePlan.loan.amount.value
export const getLoanAmountErrors = (state) => state.loan.coveragePlan.loan.amount.errors
export const getLoanPeriod = (state) => state.loan.coveragePlan.loan.term.value
export const getLoanPeriodAll = (state) => state.loan.coveragePlan.loan.term
export const getLoanPeriodErrors = (state) => state.loan.coveragePlan.loan.term.errors
export const getLoan = (state) => state.loan
export const getInterestRate = (state) => state.loan.coveragePlan.loan.interestRate
export const getInterestRateValue = (state) => state.loan.coveragePlan.loan.interestRate.value
export const getSumAssuredContract = (state) => state.loan.coveragePlan.loan.sumAssuredContract

export const getAvailableCoveragePeriods = (state) => state.loan.coveragePlan.coverage.availableTerms
export const getPaymentMethod = (state) => state.loan.coveragePlan.coverage.paymentMethod
export const getSumAssured = (state) => state.loan.coveragePlan.coverage.sumAssured.value
export const getSumAssuredErrors = (state) => state.loan.coveragePlan.coverage.sumAssured.errors
export const getCoveragePeriod = (state) => state.loan.coveragePlan.coverage.term.value
export const getCoveragePeriodAll = (state) => state.loan.coveragePlan.coverage.term
export const getCoveragePeriodErrors = (state) => state.loan.coveragePlan.coverage.term.errors
export const getPremium = (state) => state.loan.coveragePlan.coverage.premium

export const getPremiumPerMonthGLTSP = createSelector(
  getInterestRateValue,
  getLoanPeriod,
  getBasicPremium$,
  (interestRate, loanPeriod, basicPremium) => {
    const rate = interestRate / 100 / 12
    const npm = loanPeriod * 12
    const PMT = (rate * basicPremium.value * Math.pow(1 + rate, npm)) / (1 - Math.pow(1 + rate, npm))
    const premiumPerMonth = roundUp(Math.abs(PMT), -2)
    return premiumPerMonth
  }
)

export const canProceedToBenefitIllustration = createSelector(
  getSumAssured$,
  getBasicPremium$,
  getTotalPremium$,
  getBasicPlanErrorMessage,
  getSelectedRiders,
  canProceedToCoveragePlan,
  getSelectedDisplayProduct,
  (
    sumAssured$,
    basicPremium,
    totalPremium$,
    basicPlanErrorMessage,
    selectedRiders,
    canProceedToCoveragePlan,
    selectedDisplayProduct
  ) => {
    const selectedRiderInvalidMessages = selectedRiders.filter((rider) =>
      isNotNil(rider.errors.find((error) => error.type === CONSTANTS.VALIDATION))
    )

    const canProceed =
      canProceedToCoveragePlan &&
      sumAssured$.isOk &&
      basicPremium.isOk &&
      totalPremium$.isOk &&
      _.isEmpty(selectedRiderInvalidMessages) &&
      _.isEmpty(basicPlanErrorMessage)
    if (selectedDisplayProduct.type === CONSTANTS.CAMPAIGN_PRODUCT_TYPE) {
      const selectedRiderCodes = selectedRiders.map((rider) => rider.code)
      return (
        canProceed &&
        selectedDisplayProduct.requiredRiders != null &&
        selectedDisplayProduct.requiredRiders.every((riderCode) => {
          return selectedRiderCodes.indexOf(riderCode) > -1
        })
      )
    }
    return canProceed
  }
)

export const getSelectableProductMRTA = createSelector(getAge, getDisplayProducts, (age, products) => {
  const productGroup = _.filter(products, (product) =>
    _.includes(CONSTANTS.MRTA_CODE_GROUP_ACTIVE, getBasicPlanCodeForMRTA(product))
  )
  const validProducts = _.filter(productGroup, (product) => isValidAge(age.value, age.unit, _.get(product, 'entryAge')))
  const validProductCodes = validProducts.map(getBasicPlanCodeForMRTA)

  return validProductCodes
})

export const getSelectedProductMRTA = createSelector(
  getSelectedDisplayProduct,
  getSelectableProductMRTA,
  (basicPlan, productSelectable) => {
    if (basicPlan.useLegacy === false) {
      return productSelectable.includes(basicPlan.code) ? basicPlan.code : DEFAULTS.DEFAULT_MRTA_PLUS_PRODUCT_CODE
    } else {
      return productSelectable.includes(basicPlan.basicPlanCode)
        ? basicPlan.basicPlanCode
        : DEFAULTS.DEFAULT_MRTA_PLUS_PRODUCT_CODE
    }
  }
)

export const getDescriptionProductMRTA = createSelector(getSelectedDisplayProduct, (basicPlan) => {
  const basicPlanCode = getBasicPlanCodeForMRTA(basicPlan)
  switch (basicPlanCode) {
    case 'MRTA_PLUS':
      return DEFAULTS.DEFAULT_MRTA_DESCRIPTION_PRODUCT_PLAN.MRTA_PLUS
    case 'MRTA_FULL':
      return DEFAULTS.DEFAULT_MRTA_DESCRIPTION_PRODUCT_PLAN.MRTA_FULL
    case 'MRTA_TRUNCATE':
      return DEFAULTS.DEFAULT_MRTA_DESCRIPTION_PRODUCT_PLAN.MRTA_TRUNCATE
    default:
      return ''
  }
})

export const getOptionsLoanPeriod = createSelector(getSelectedDisplayProduct, (basicPlan) => {
  switch (getBasicPlanCodeForMRTA(basicPlan)) {
    case 'MRTA_SIMPLY':
    case 'MRTA_PLUS':
    case 'MRTA_FULL':
      return DEFAULTS.DEFAULT_LOAN_PERIOD.MRTA
    case 'MRTA_TRUNCATE':
      return DEFAULTS.DEFAULT_LOAN_PERIOD.MRTA.filter((v) => v.value >= 10 && v.value <= 30)
    case 'GLTSP':
      return DEFAULTS.DEFAULT_LOAN_PERIOD.GLTSP
    default:
      return []
  }
})

export const getOptionsCoveragePeriod = createSelector(getSelectedDisplayProduct, getAge, (basicPlan, age) => {
  const basicPlanCode = getBasicPlanCodeForMRTA(basicPlan)
  const rangeCoveragePeriod = DEFAULTS.DEFAULT_MRTA_GLTSP_RANGE_COVERAGE_PERIOD[basicPlanCode]
  const maxAgeCoverage = 70 - age.value
  const minCoverage = rangeCoveragePeriod.minimum
  const maxCoverage = [
    DEFAULTS.DEFAULT_MRTA_SIMPLY_PRODUCT_CODE,
    DEFAULTS.DEFAULT_MRTA_PLUS_PRODUCT_CODE,
    DEFAULTS.DEFAULT_MRTA_FULL_PRODUCT_CODE,
    DEFAULTS.DEFAULT_GLTSP_PRODUCT_CODE,
  ].includes(basicPlanCode)
    ? maxAgeCoverage >= rangeCoveragePeriod.maximum
      ? rangeCoveragePeriod.maximum
      : maxAgeCoverage
    : rangeCoveragePeriod.maximum

  const result = DEFAULTS.DEFAULT_LOAN_PERIOD.GLTSP.filter(
    (period) => period.value >= minCoverage && period.value <= maxCoverage
  )
  return result
})

export const getPercentSumAssured = createSelector(getSumAssured$, getLoanAmount, (sumAssrued, loanAmount) => {
  return Math.ceil((sumAssrued.value / loanAmount) * 100)
})

export const getPercentCoveragePeriod = createSelector(
  getCoveragePeriod,
  getLoanPeriod,
  (coveragePeriod, loanPeriod) => {
    return Math.ceil((coveragePeriod / loanPeriod) * 100)
  }
)

export const isMRTAProduct = createSelector(
  getSelectedDisplayProduct,
  (basicPlan) =>
    basicPlan.category === CONSTANTS.MRTA && getBasicPlanCodeForMRTA(basicPlan) !== CONSTANTS.GLTSP_CODE.GLTSP
)

export const isMRTAGroup = (basicPlanCode) => {
  return Object.values(CONSTANTS.MRTA_CODE).includes(basicPlanCode)
}
export const isGLTSPGroup = (basicPlanCode) => {
  return Object.values(CONSTANTS.GLTSP_CODE).includes(basicPlanCode)
}
export const getGLTSPPlancode = (planCode, sumAssuredContract) => {
  if (sumAssuredContract < 500000) {
    return planCode.substring(0, 5) + 'A'
  } else {
    return planCode.substring(0, 5) + 'B'
  }
}

export const isGLTSPProduct = createSelector(
  getSelectedDisplayProduct,
  (basicPlan) =>
    basicPlan.category === CONSTANTS.MRTA && getBasicPlanCodeForMRTA(basicPlan) === CONSTANTS.GLTSP_CODE.GLTSP
)

export const getMRTATRUNCATELoanPeriod = (loanPeriod, age) => {
  return Math.min(loanPeriod, CONSTANTS.MRTA_TRUNCATE_MAX_LOAN_INSURE_AGE - age.value)
}
